import {useLayoutEffect} from 'react';

import useIsMobileViewport from '../../hooks/useIsMobileViewport';

const PORTAL_SELECTOR = [
    '#root',
    '.ant-drawer',
    '.ant-modal-root',
    '.ant-popover',
    '.ant-dropdown',
    '.ant-select-dropdown',
    '.ant-picker-dropdown',
    '.ant-tooltip',
    '.ant-message',
    '.ant-notification',
    '.mobile-notification-message',
].join(', ');
const CONTROL_SELECTOR = 'button, a[href], input, select, textarea, [tabindex="0"]';
const CLOSE_SELECTOR = '.mobile-navigation-close, .notifications-panel-close, .ant-drawer-close, .ant-modal-close';

const isVisible = (element) =>
    element.getClientRects().length > 0 &&
    getComputedStyle(element).visibility !== 'hidden' &&
    !element.closest('[inert], [aria-hidden="true"]');

const getActiveDialog = () => {
    const dialogs = Array.from(document.querySelectorAll('[role="dialog"]')).filter((dialog) => {
        const drawer = dialog.closest('.ant-drawer');

        return (!drawer || drawer.classList.contains('ant-drawer-open')) && isVisible(dialog);
    });
    const getLayer = (dialog) => {
        const overlay = dialog.closest('.ant-drawer, .ant-modal-wrap');

        return Number.parseInt(getComputedStyle(overlay || dialog).zIndex, 10) || 0;
    };

    return dialogs.sort((left, right) => getLayer(left) - getLayer(right)).pop();
};

const MobileFocusBoundary = ({children}) => {
    const isMobile = useIsMobileViewport();

    useLayoutEffect(() => {
        if (!isMobile) return undefined;

        const handleFocus = (event) => {
            const target = event.target;

            if (!(target instanceof Element)) return;
            if (target !== document.body && !target.closest(PORTAL_SELECTOR)) return;
            if (target.matches('input, textarea') || target.isContentEditable || target.form) return;

            // Keep native focus/CSS/accessibility, but isolate non-editable UI focus from
            // document-level form/autofill listeners. Editable/form focus continues normally.
            event.stopPropagation();
        };
        const handleKeyDown = (event) => {
            if (event.key !== 'Tab' && event.key !== 'Escape') return;

            const dialog = getActiveDialog();

            if (!dialog) return;
            if (event.key === 'Escape') {
                // When opening by touch, focus can still be on the page's trigger.
                if (!dialog.contains(event.target)) dialog.querySelector(CLOSE_SELECTOR)?.click();
                return;
            }
            const controls = Array.from(dialog.querySelectorAll(CONTROL_SELECTOR)).filter(
                (control) => !control.matches(':disabled') && control.tabIndex >= 0 && isVisible(control)
            );
            const activeIndex = controls.indexOf(document.activeElement);
            const nextIndex = event.shiftKey
                ? activeIndex <= 0
                    ? controls.length - 1
                    : activeIndex - 1
                : (activeIndex + 1) % controls.length;

            if (!controls.length) return;
            event.preventDefault();
            // React controls handle Tab first (e.g. dismiss an open category list).
            // Keep focus on visible controls so the hidden library sentinels are skipped.
            event.stopPropagation();
            controls[nextIndex].focus();
        };

        window.addEventListener('focus', handleFocus, true);
        window.addEventListener('focusin', handleFocus, true);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('focus', handleFocus, true);
            window.removeEventListener('focusin', handleFocus, true);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isMobile]);

    return children;
};

export default MobileFocusBoundary;
